/**
 * Insights — each analyzed match as a story you tap through.
 *
 * THE SLIDES ARE PRE-GENERATED. scripts/generate_story.py writes story.json
 * and the slide PNGs to R2 per game; this page reads a manifest and shows
 * images. Nothing is rendered on the fly, which is why a page of 40 matches
 * costs one listing rather than 40 renders.
 *
 * THE PLAYER IS A DIALOG, and is built as one on purpose: Escape closes it,
 * focus moves into it, and arrow keys move between slides. The Jinja version
 * had tap zones and nothing else, so it was unusable from a keyboard.
 *
 * NO AUTOPLAY TIMER. The original advanced on a timer like a social story;
 * these slides carry a paragraph of tactical reading, and a slide that leaves
 * before it has been read is worse than one that waits.
 */
import { ArrowLeft, ArrowRight, X } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'

import { ApiError, api } from '../lib/api'
import { pathSeg } from '../lib/urls'
import InsightsCards from './InsightsCards'
import InsightsFeatured from './InsightsFeatured'
import InsightsReports from './InsightsReports'
import './admin.css'
import './insights.css'

type FeedGame = {
  game_code: string
  match_date: string | null
  generated_at: string
  tier: string
  slide_count: number
  thumb: string
}
type Slide = { url: string; title?: string; kicker?: string; read?: string }
type Story = {
  slides: Slide[]
  match_date: string | null
  game_url?: string | null
  recap_url?: string | null
  tier?: string
}

const day = (g: FeedGame) =>
  (g.match_date ?? g.generated_at).slice(0, 10)

export default function Insights() {
  const [games, setGames] = useState<FeedGame[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [open, setOpen] = useState<FeedGame | null>(null)
  const [reportCount, setReportCount] = useState(0)
  const [cardCount, setCardCount] = useState(0)

  useEffect(() => {
    api<{ games: FeedGame[] }>('/api/v1/feed')
      .then((d) => setGames(d.games))
      .catch((e: unknown) => setError(e instanceof ApiError ? e.message : String(e)))
  }, [])

  if (error) {
    return (<><h1 className="page-title">Insights Feed</h1><p className="error">{error}</p></>)
  }
  // One title in every state — the rail calls this the Insights Feed, and the
  // page said "Insights" empty but "Match insights" full (one idea, one name).
  if (!games) {
    return (<><h1 className="page-title">Insights Feed</h1><p className="muted">Loading…</p></>)
  }

  if (games.length === 0) {
    return (
      <>
        <h1 className="page-title">Insights Feed</h1>
        <InsightsCards onCount={setCardCount} />
        <InsightsFeatured />
        <InsightsReports onCount={setReportCount} />
        {/* NO "NOTHING HERE" LINE (Matt, 2026-10-14). Two terse notices used
            to live here — "No match stories yet." under content, and "No
            match insights yet." above the panel. Both told the reader what
            was ALREADY obvious from the page, and the first read as a
            complaint printed under a perfectly good card.

            The explanatory panel stays for a club with no cards and no
            reports: a page that is blank because the data has not arrived is
            indistinguishable from one that is broken, and that sentence is
            the difference. A club with a card or a report needs no notice —
            it can see what it has. */}
        {reportCount === 0 && cardCount === 0 && (
          <div className="panel ins-empty">
            <p style={{ font: 'var(--text-body)', color: 'var(--color-text-body)',
                        margin: 0, maxWidth: '64ch' }}>
              A match appears here once its tracking data has been analyzed and its
              story generated. Nothing is wrong — there is just nothing to read yet.
            </p>
          </div>
        )}
      </>
    )
  }

  return (
    <>
      <h1 className="page-title">Insights Feed</h1>
      <InsightsCards />
      <InsightsFeatured />
      <InsightsReports />
      <p className="page-sub">
        {games.length} match{games.length === 1 ? '' : 'es'} analyzed. Open one
        to read through what happened.
      </p>

      <div className="ins-grid">
        {games.map((g) => (
          <button className="ins-card" key={g.game_code} onClick={() => setOpen(g)}>
            <span className="ins-thumb">
              <img src={g.thumb} alt="" loading="lazy" />
              <span className="ins-count">{g.slide_count}</span>
            </span>
            <span className="ins-foot">
              <span className="adm-primary">{g.game_code}</span>
              <span className="adm-when">{day(g)}</span>
            </span>
          </button>
        ))}
      </div>

      {open && <StoryPlayer game={open} onClose={() => setOpen(null)} />}
    </>
  )
}

function StoryPlayer({ game, onClose }: { game: FeedGame; onClose: () => void }) {
  const [story, setStory] = useState<Story | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [i, setI] = useState(0)
  const dialog = useRef<HTMLDivElement>(null)

  useEffect(() => {
    api<Story>(`/api/v1/feed/story/${pathSeg(game.game_code)}`)
      .then(setStory)
      .catch((e: unknown) => setError(e instanceof ApiError ? e.message : String(e)))
  }, [game.game_code])

  const count = story?.slides.length ?? 0
  const next = useCallback(() => setI((n) => Math.min(n + 1, count - 1)), [count])
  const prev = useCallback(() => setI((n) => Math.max(n - 1, 0)), [])

  useEffect(() => {
    dialog.current?.focus()
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
      else if (e.key === 'ArrowRight') next()
      else if (e.key === 'ArrowLeft') prev()
    }
    window.addEventListener('keydown', onKey)
    // The page behind must not scroll while a full-screen dialog is up.
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = prevOverflow
    }
  }, [next, prev, onClose])

  const slide = story?.slides[i]

  return (
    <div className="ins-scrim" onClick={onClose}>
      <div
        className="ins-stage"
        ref={dialog}
        role="dialog"
        aria-modal="true"
        aria-label={`${game.game_code} match insights`}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="ins-bars">
          {Array.from({ length: count }, (_, n) => (
            <span key={n} className={`ins-bar${n < i ? ' is-done' : n === i ? ' is-current' : ''}`} />
          ))}
        </div>

        <div className="ins-top">
          <div>
            <div className="ins-match">{game.game_code}</div>
            <div className="ins-meta">
              {story?.match_date ?? day(game)}
              {count > 0 ? ` · ${i + 1} of ${count}` : ''}
            </div>
          </div>
          <button className="ins-close" onClick={onClose} aria-label="Close">
            <X size={20} />
          </button>
        </div>

        {error && <p className="ins-body error">{error}</p>}
        {!story && !error && <p className="ins-body muted">Loading…</p>}

        {slide && (
          <div className="ins-slide">
            {slide.kicker && <div className="ins-kicker">{slide.kicker}</div>}
            {slide.title && <h3>{slide.title}</h3>}
            <div className="ins-viz"><img src={slide.url} alt={slide.title ?? ''} /></div>
            {slide.read && <p className="ins-read">{slide.read}</p>}
          </div>
        )}

        <div className="ins-actions">
          <button className="adm-btn adm-btn--quiet" onClick={prev} disabled={i === 0}>
            <ArrowLeft size={14} /> Back
          </button>
          <button className="adm-btn adm-btn--quiet" onClick={next}
                  disabled={count === 0 || i >= count - 1}>
            Next <ArrowRight size={14} />
          </button>
          <span className="adm-bar__spacer" />
          {story?.game_url && (
            <a className="adm-btn" href={story.game_url}>Match breakdown</a>
          )}
          {story?.recap_url && (
            <a className="adm-btn adm-btn--quiet" href={story.recap_url}>Recap notebook</a>
          )}
        </div>
      </div>
    </div>
  )
}
